import { DateTime } from 'luxon';
import { INDICATOR_DAY_INTERVAL_TYPE, type IndicatorDayIntervalType } from '../../constants/business/indicator';

/**
 * Calculates the number of days between two dates based on the interval type,
 * returning fractional days for precise time differences.
 *
 * For example:
 * - From 2024-01-15T09:00:00Z to 2024-01-15T21:00:00Z = 0.5 days (12 hours)
 * - From 2024-01-15T09:00:00Z to 2024-01-16T09:00:00Z = 1.0 days (24 hours)
 * - From 2024-01-15T09:00:00Z to 2024-01-16T15:00:00Z = 1.25 days (30 hours)
 *
 * @param intervalType - The type of interval to calculate (BUSINESS or CALENDAR)
 * @param startDate - The start date
 * @param endDate - The end date (defaults to current date if not provided)
 * @returns The number of days between the dates (can be fractional)
 */
export function calculateDaysDuration(
    intervalType: IndicatorDayIntervalType,
    startDate: Date,
    endDate?: Date | null,
): number {
    const start = DateTime.fromJSDate(startDate);
    const end = endDate ? DateTime.fromJSDate(endDate) : DateTime.now();

    // Ensure both dates are valid
    if (!start.isValid || !end.isValid) {
        throw new Error('Invalid date provided to calculateDaysBetweenMilestones');
    }

    if (intervalType === INDICATOR_DAY_INTERVAL_TYPE.BUSINESS) {
        return calculateBusinessDaysDifference(start, end);
    } else {
        // For calendar days, use Luxon's diff method to get fractional days
        const diff = end.diff(start, 'days');
        return diff.days;
    }
}

/**
 * Adds a specified number of business days to a date.
 * Business days are Monday through Friday.
 *
 * @param date - The starting date
 * @param days - Number of business days to add (can be negative)
 * @param options.zone - IANA zone whose weekdays count; the process zone when omitted
 * @returns The resulting date
 */
export function addBusinessDays(date: Date, days: number, options?: { zone?: string }): Date {
    let dt = DateTime.fromJSDate(date, { zone: options?.zone });
    let addedDays = 0;
    const increment = days > 0 ? 1 : -1;

    while (addedDays !== days) {
        dt = dt.plus({ days: increment });
        if (dt.weekday <= 5) {
            // Monday through Friday
            addedDays += increment;
        }
    }

    return dt.toJSDate();
}

/**
 * Calculates the difference in business days between two dates.
 * Returns whole days only (fractional parts are truncated).
 * Note: This follows date-fns convention where the order is (endDate, startDate)
 *
 * @param endDate - The end date (first parameter for date-fns compatibility)
 * @param startDate - The start date (second parameter for date-fns compatibility)
 * @returns Number of business days between the dates
 */
export function differenceInBusinessDays(endDate: Date, startDate: Date): number {
    const start = DateTime.fromJSDate(startDate).startOf('day');
    const end = DateTime.fromJSDate(endDate).startOf('day');

    // If the end date is before the start date, return negative
    if (end < start) {
        return -differenceInBusinessDays(startDate, endDate);
    }

    let businessDays = 0;
    let current = start;

    // Count all weekdays from start to end (exclusive of end)
    while (current < end) {
        if (current.weekday <= 5) {
            businessDays++;
        }
        current = current.plus({ days: 1 });
    }

    return businessDays;
}

/**
 * Calculates the difference in calendar days between two dates.
 * Returns whole days only (fractional parts are truncated).
 * Note: This follows date-fns convention where the order is (endDate, startDate)
 *
 * @param endDate - The end date (first parameter for date-fns compatibility)
 * @param startDate - The start date (second parameter for date-fns compatibility)
 * @returns Number of calendar days between the dates
 */
export function differenceInCalendarDays(endDate: Date, startDate: Date): number {
    const start = DateTime.fromJSDate(startDate);
    const end = DateTime.fromJSDate(endDate);
    const diff = end.diff(start, 'days');
    return Math.floor(diff.days);
}

/**
 * Returns the end of a day for the given date.
 *
 * @param date - The date to get end of day for
 * @returns A new date set to 23:59:59.999 of the same day
 */
export function endOfDay(date: Date): Date {
    return DateTime.fromJSDate(date).toUTC().endOf('day').toJSDate();
}

/**
 * Checks if a date is in the past.
 *
 * @param date - The date to check
 * @returns True if the date is before the current moment
 */
export function isPast(date: Date): boolean {
    return DateTime.fromJSDate(date) < DateTime.now();
}

/**
 * Checks if a date is within the given interval.
 *
 * @param date - The date to check
 * @param interval - The interval with start and end dates
 * @returns True if the date is within or equal to the interval boundaries
 */
export function isWithinInterval(date: Date, interval: { start: Date; end: Date }): boolean {
    const dt = DateTime.fromJSDate(date);
    const start = DateTime.fromJSDate(interval.start);
    const end = DateTime.fromJSDate(interval.end);
    return dt >= start && dt <= end;
}

/**
 * Adds a specified number of days to a date.
 *
 * @param date - The starting date
 * @param days - Number of days to add (can be negative)
 * @returns The resulting date
 */
export function addDays(date: Date, days: number): Date {
    return DateTime.fromJSDate(date).plus({ days }).toJSDate();
}

/**
 * Whether a valid instant is the UTC-noon encoding of a date-only value, as `DatePicker` and
 * `RangePicker` write a calendar day with `dateOnly`. Read in UTC it names the same day anywhere.
 */
export function isCalendarDayInstant(date: Date): boolean {
    return (
        !Number.isNaN(date.getTime()) &&
        date.getUTCHours() === 12 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0 &&
        date.getUTCMilliseconds() === 0
    );
}

/**
 * Calculates business days difference considering exact time, returning fractional days.
 * Only counts time during business days (Monday-Friday).
 */
function calculateBusinessDaysDifference(start: DateTime, end: DateTime): number {
    // If the end date is before the start date, return negative
    if (end < start) {
        return -calculateBusinessDaysDifference(end, start);
    }

    // If same day, calculate fractional day if it's a business day
    if (start.hasSame(end, 'day')) {
        if (start.weekday <= 5) {
            // Monday through Friday
            const diff = end.diff(start, 'days');
            return diff.days;
        }
        return 0; // Weekend days don't count
    }

    let businessDays = 0;

    // Start from the day after the start date for counting full days
    let current = start.startOf('day').plus({ days: 1 });
    const endDay = end.startOf('day');

    // Count full business days between start and end (exclusive)
    while (current <= endDay) {
        if (current.weekday <= 5) {
            // Monday through Friday
            businessDays += 1;
        }
        current = current.plus({ days: 1 });
    }

    // For fractional calculation, we need to handle the partial days differently
    // Calculate time on the first day (if it's a business day)
    if (start.weekday <= 5) {
        const endOfFirstDay = start.endOf('day');
        const firstDayTime = endOfFirstDay.diff(start, 'days').days;
        businessDays += firstDayTime;
    }

    // Calculate time on the last day (if it's a business day and not the same as start day)
    if (!start.hasSame(end, 'day') && end.weekday <= 5) {
        const startOfLastDay = end.startOf('day');
        const lastDayTime = end.diff(startOfLastDay, 'days').days;
        // Subtract 1 since we counted this day in the full days loop
        businessDays += lastDayTime - 1;
    }

    return businessDays;
}
